import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Chip, Select, TextField } from '@nyce/admin-kit/ui';
import { Loading, Page, useAsync } from '../../components/common';
import { TenantPicker } from '../../identity/TenantPicker';
import { loadTargets } from '../../identity/session';
import type { DiscoveredInstance, EnvironmentKind } from '../../api';
import { api, client } from '../../session';
import { db } from '../../api/db';
import { formatRelative } from '../../lib/format';
import { formFromInstance, toDbServerInput } from '../../pages/admin/DbServers';
import { useOnboardingConfig } from '../api/config';
import { slug } from '../api/directory';
import type { GeneratedFile } from '../generate/files';
import { Wizard, type WizardStep } from '../wizard/Wizard';
import { FileOutput } from '../wizard/FileOutput';
import { useWizardParam } from '../wizard/params';
import { Facts, Note, StepActions, useAction } from '../wizard/parts';

const KINDS: EnvironmentKind[] = ['production', 'test', 'staging', 'dev'];

/** What a new `nyce-hosting-<customer>` repo's workflow needs (the hub does not deploy installations). */
export function hostingValues(v: {
  namespace: string;
  database: string;
  uri: string;
  name: string;
}): GeneratedFile {
  return {
    name: 'hosting-env.yml',
    content: `# env of .github/workflows/<customer>.<kind>.commit.yml in nyce-hosting-<customer>
env:
  NAMESPACE: ${v.namespace}
  DATABASE: ${v.database}
  URI: ${v.uri}
  NAME: ${v.name}
`,
    note: 'Copy into the new hosting repo (template: nyce-hosting-kjell), together with its manifests.',
  };
}

/** W2: register an installation — cluster, namespaces, database, environment, AUTH tenant and backups. */
export function InstallationWizardPage() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    void loadTargets().then(() => setReady(true));
  }, []);
  return (
    <Page
      title="Installation"
      description="Register a customer installation in the hub: its cluster and namespaces, its database (from a discovered instance), the environment with its AUTH tenant, and nightly backups. Logs, pods, databases and statistics then pivot on it."
    >
      {ready ? <InstallationWizard /> : <Loading loading />}
    </Page>
  );
}

function InstallationWizard() {
  const config = useOnboardingConfig();
  const [clusterId, setClusterId] = useWizardParam('cluster');
  const [nsParam, setNs] = useWizardParam('ns');
  const [serverId, setServerId] = useWizardParam('server');
  const [dbName, setDbName] = useWizardParam('db');
  const [envId, setEnvId] = useWizardParam('env');
  const namespaces = nsParam ? nsParam.split(',') : [];
  const [instance, setInstance] = useState('');
  const [cred, setCred] = useState({ username: '', password: '' });
  const [form, setForm] = useState({
    name: '',
    customer: '',
    kind: 'production' as EnvironmentKind,
    baseUrl: '',
    authTenantId: '',
  });

  const reg = useAsync(async () => {
    const [clusters, servers, envs, discovery, schedules] = await Promise.all([
      api.admin.clusters.list(),
      api.admin.dbServers.list(),
      api.admin.environments.list(),
      api.admin.discovery().catch(() => null),
      db.schedules.list().catch(() => []),
    ]);
    return { clusters, servers, envs, discovery, schedules };
  }, []);
  const nsList = useAsync(
    () =>
      clusterId
        ? client.get<string[]>(`/admin/clusters/${encodeURIComponent(clusterId)}/namespaces`)
        : Promise.resolve([]),
    [clusterId]
  );
  const dbs = useAsync(
    () => (serverId ? api.admin.dbServers.databases(serverId) : Promise.resolve([] as string[])),
    [serverId]
  );
  const action = useAction(async () => {
    await reg.reload();
    await dbs.reload();
  });
  const r = reg.data;
  const cluster = r?.clusters.find((c) => c.id === clusterId);
  const server = r?.servers.find((s) => s.id === serverId);
  const env = r?.envs.find((e) => e.id === envId);
  const claimed = new Set(
    (r?.envs ?? []).filter((e) => e.clusterId === clusterId && e.id !== envId).flatMap((e) => e.namespaces)
  );
  const freeInstances = (r?.discovery?.instances ?? []).filter((i) => !i.serverId && i.role === 'primary');
  const backup = (r?.schedules ?? []).find(
    (s) => s.kind === 'backup' && s.backup?.serverId === serverId && s.backup.database === dbName
  );
  const defaultId = slug(namespaces[0] ?? '');
  const toggleNs = (n: string) =>
    setNs((namespaces.includes(n) ? namespaces.filter((x) => x !== n) : [...namespaces, n]).join(','));

  const steps: WizardStep[] = [
    {
      id: 'cluster',
      title: 'Cluster',
      state: cluster ? 'done' : 'todo',
      summary: cluster ? `${cluster.name} (${cluster.environment})` : undefined,
      body: (
        <>
          <p>
            The GKE cluster the installation runs in. Clusters are discovered in GCP; none here means the hub
            cannot see the project yet.
          </p>
          <Select
            label="Cluster"
            value={clusterId}
            onChange={(v) => {
              setClusterId(v);
              setNs('');
            }}
            options={[
              { value: '', label: '— pick —' },
              ...(r?.clusters ?? []).map((c) => ({
                value: c.id,
                label: `${c.name || c.id} (${c.environment})`,
              })),
            ]}
          />
        </>
      ),
    },
    {
      id: 'namespaces',
      title: 'Namespaces',
      state: !cluster ? 'blocked' : namespaces.length ? 'done' : 'todo',
      blocked: 'Pick the cluster first.',
      summary: namespaces.join(', ') || undefined,
      body: (
        <>
          <p>
            The installation's Kubernetes namespaces (e.g. <code>nlc-prod-acme</code>). A new installation's
            namespace is created by its hosting repo's first deploy — come back once it exists.
          </p>
          <Loading loading={nsList.loading} />
          <div className="ob-checks">
            {(nsList.data ?? [])
              .filter((n) => !/^(kube-|gke-|gmp-|default$|config-management)/.test(n))
              .map((n) => (
                <button
                  key={n}
                  type="button"
                  className="ob-chip-btn"
                  aria-pressed={namespaces.includes(n)}
                  disabled={claimed.has(n)}
                  title={claimed.has(n) ? 'Claimed by another environment' : undefined}
                  onClick={() => toggleNs(n)}
                >
                  <Chip size="sm" className={namespaces.includes(n) ? 'oph-status nyk-tone-blue' : undefined}>
                    {namespaces.includes(n) ? '✓ ' : ''}
                    {n}
                  </Chip>
                </button>
              ))}
          </div>
          {nsList.error && <Note tone="warn">{nsList.error}</Note>}
        </>
      ),
    },
    {
      id: 'database',
      title: 'Database',
      state: server && dbName ? 'done' : 'todo',
      summary: server && dbName ? `${server.name} / ${dbName}` : undefined,
      body: (
        <>
          <p>
            The installation's Postgres server and database. A new Cloud SQL or AlloyDB instance is registered
            from discovery (production instances are protected: never a copy target); only user and password
            are typed.
          </p>
          <Select
            label="DB server"
            value={serverId}
            onChange={(v) => {
              setServerId(v);
              setDbName('');
            }}
            options={[
              { value: '', label: '— pick or register —' },
              ...(r?.servers ?? []).map((s) => ({
                value: s.id,
                label: `${s.name} (${s.environment}${s.protected ? ', protected' : ''})`,
              })),
            ]}
          />
          {!serverId && !!freeInstances.length && (
            <>
              <div className="nyk-form">
                <Select
                  label="Discovered instance"
                  value={instance}
                  onChange={setInstance}
                  options={[
                    { value: '', label: '— pick —' },
                    ...freeInstances.map((i: DiscoveredInstance) => ({
                      value: i.resourceName,
                      label: `${i.name} (${i.kind}, ${i.projectId})`,
                    })),
                  ]}
                />
                <div />
                <TextField
                  label="User"
                  value={cred.username}
                  onChange={(e) => setCred({ ...cred, username: e.target.value })}
                />
                <TextField
                  label="Password"
                  type="password"
                  value={cred.password}
                  onChange={(e) => setCred({ ...cred, password: e.target.value })}
                  autoComplete="new-password"
                />
              </div>
              <StepActions error={action.error}>
                <Button
                  disabled={!instance || !cred.username || !cred.password}
                  loading={action.busy === 'server'}
                  onClick={() =>
                    void action.run('server', async () => {
                      const i = freeInstances.find((x) => x.resourceName === instance)!;
                      const f = { ...formFromInstance(i), username: cred.username, password: cred.password };
                      const saved = await api.admin.dbServers.put(f.id, toDbServerInput(f));
                      const t = await api.admin.dbServers.test(saved.id);
                      if (!t.ok)
                        throw new Error(
                          `Registered ${saved.name}, but the connection failed: ${t.error ?? 'unknown error'}`
                        );
                      setCred({ username: '', password: '' });
                      setServerId(saved.id);
                    })
                  }
                >
                  Register and test
                </Button>
              </StepActions>
            </>
          )}
          {serverId && (
            <Select
              label="Database"
              value={dbName}
              onChange={setDbName}
              searchable
              options={[
                { value: '', label: dbs.loading ? 'loading…' : '— pick —' },
                ...(dbs.data ?? []).map((d) => ({ value: d, label: d })),
              ]}
              hint={
                dbs.error
                  ? `Could not list databases: ${dbs.error}`
                  : 'Created by the hosting repo / restore, not by the hub.'
              }
            />
          )}
        </>
      ),
    },
    {
      id: 'environment',
      title: 'Environment',
      state: !cluster || !namespaces.length ? 'blocked' : env ? 'done' : 'todo',
      blocked: 'Pick the cluster and namespaces first.',
      summary: env ? `${env.name} (${env.id})` : undefined,
      body: env ? (
        <Facts
          rows={[
            ['Environment', `${env.name} (${env.id})`],
            ['Customer', env.customer || '—'],
            ['Namespaces', env.namespaces.join(', ')],
            ['Database', env.dbServerId ? `${env.dbServerName ?? env.dbServerId} / ${env.dbName}` : '—'],
            ['AUTH tenant', env.authTenantId ?? '—'],
          ]}
        />
      ) : (
        <>
          <div className="nyk-form">
            <TextField
              label="Id"
              value={envId || defaultId}
              onChange={(e) => setEnvId(slug(e.target.value))}
              hint="Slug; cannot be changed."
            />
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <TextField
              label="Customer"
              value={form.customer}
              onChange={(e) => setForm({ ...form, customer: e.target.value })}
            />
            <Select
              label="Kind"
              value={form.kind}
              onChange={(k) => setForm({ ...form, kind: k as EnvironmentKind })}
              options={KINDS.map((k) => ({ value: k, label: k }))}
            />
            <TextField
              label="Base URL"
              className="nyk-url"
              value={form.baseUrl}
              onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
              placeholder="https://acme.nycelogic.com"
            />
            <TenantPicker
              target={config?.hubTarget}
              value={form.authTenantId}
              onChange={(authTenantId) => setForm({ ...form, authTenantId })}
              hint="Created with the Customer & tenant wizard."
            />
          </div>
          <StepActions error={action.error}>
            <Button
              disabled={!(envId || defaultId) || !form.name.trim()}
              loading={action.busy === 'env'}
              onClick={() =>
                void action.run('env', async () => {
                  const id = envId || defaultId;
                  if (r?.envs.some((e) => e.id === id)) throw new Error(`Environment ${id} exists already.`);
                  await api.admin.environments.put(id, {
                    name: form.name.trim(),
                    customer: form.customer.trim(),
                    kind: form.kind,
                    clusterId,
                    namespaces,
                    dbServerId: serverId || null,
                    dbName: serverId ? dbName : '',
                    baseUrl: form.baseUrl.trim(),
                    notes: 'Registered with the onboarding wizard.',
                    enabled: true,
                    authTenantId: form.authTenantId || null,
                  });
                  setEnvId(id);
                })
              }
            >
              Register environment
            </Button>
          </StepActions>
        </>
      ),
    },
    {
      id: 'backups',
      title: 'Backups',
      state: !server || !dbName ? 'blocked' : backup ? 'done' : 'todo',
      blocked: 'Pick the database first.',
      summary: backup ? `${backup.cron} ${backup.timezone}` : undefined,
      body: (
        <>
          <p>A nightly backup of the database (02:00 Stockholm time) to the hub's backup bucket.</p>
          {backup && (
            <Facts
              rows={[
                ['Schedule', `${backup.name}: ${backup.cron} (${backup.timezone})`],
                ['Next run', backup.nextRunAt ? formatRelative(backup.nextRunAt) : '—'],
                ['Last run', backup.lastRunAt ? formatRelative(backup.lastRunAt) : 'never'],
              ]}
            />
          )}
          <StepActions error={action.error}>
            {!backup && (
              <Button
                loading={action.busy === 'backup'}
                onClick={() =>
                  void action.run('backup', () =>
                    db.schedules.put(`backup-${slug(dbName)}`, {
                      name: `Nightly backup ${dbName}`,
                      kind: 'backup',
                      cron: '0 2 * * *',
                      timezone: 'Europe/Stockholm',
                      backup: { serverId, database: dbName },
                      configId: null,
                      enabled: true,
                    })
                  )
                }
              >
                Create nightly backup
              </Button>
            )}
            {backup && (
              <Button
                variant="ghost"
                loading={action.busy === 'run'}
                onClick={() => void action.run('run', () => db.schedules.run(backup.id))}
              >
                Run a backup now
              </Button>
            )}
            <Link className="nyk-link" to="/databases?tab=copy">
              Copy configurations (prod → test)
            </Link>
          </StepActions>
        </>
      ),
    },
    {
      id: 'hosting',
      title: 'Hosting repo values',
      state: namespaces.length ? 'done' : 'blocked',
      blocked: 'Pick the namespaces first.',
      body: (
        <>
          <p>
            The installation itself is deployed by its <code>nyce-hosting-&lt;customer&gt;</code> repo. These
            are the values its workflow needs; the WorkServer's AUTH client comes from the{' '}
            <Link className="nyk-link" to="/onboarding/tenant">
              Customer & tenant wizard
            </Link>
            .
          </p>
          <FileOutput
            files={[
              hostingValues({
                namespace: namespaces[0] ?? '<namespace>',
                database: dbName || '<database>',
                uri:
                  (env?.baseUrl || form.baseUrl).replace(/^https?:\/\//, '').replace(/\/.*$/, '') || '<host>',
                name: env?.name || form.name || '<name>',
              }),
            ]}
          />
        </>
      ),
    },
  ];

  return (
    <Wizard steps={steps} loading={reg.loading && !r} error={reg.error} onRecheck={() => void reg.reload()} />
  );
}
